// Mobile (below md) card for one master-product family in the list screen —
// same data and per-row actions as the read-mode ExcelTable row, presented as
// a stacked card (pattern: pages/sales-lookup/components/family-card.tsx and
// pages/gpo-orders GpoOrderCard).
import type { MasterProductFamily } from 'apis/master-product-families';
import type { DealCategoryTreeNode } from 'apis/supplier-prices';
import { Box, Flex, HStack, Icon, IconButtonWithTooltip, PenIcon, Spacer, Text } from 'design-system';
import { FiGitMerge, FiList, FiTrash2 } from 'react-icons/fi';
import { DeleteFamilyModal } from './delete-family-modal';
import { EditFamilyModal } from './edit-family-modal';
import { FamilyActiveCell } from './table-cells/family-active-cell';
import { FamilyCategoryCell } from './table-cells/family-category-cell';
import { formatFamilyDate } from './table-cells/family-date-cell';
import { FamilyPhotoCell } from './table-cells/family-photo-cell';

export interface FamilyListCardProps {
  family: MasterProductFamily;
  categoryTree: DealCategoryTreeNode[];
  onOpen: () => void;
  onMerge: () => void;
}

export const FamilyListCard = ({ family, categoryTree, onOpen, onMerge }: FamilyListCardProps) => {
  const dateLine =
    family.createdAt !== family.updatedAt
      ? `Updated ${formatFamilyDate(family.updatedAt)}`
      : `Created ${formatFamilyDate(family.createdAt)}`;

  return (
    <Box bg='white' borderRadius='14px' border='1px solid' borderColor='gray.100' p='14px' mb='10px'>
      <Flex align='flex-start' gap='12px'>
        <FamilyPhotoCell imageUrl={family.imageUrl} variant='thumb' />

        <Box flex='1' minW={0}>
          <Text
            as='button'
            textAlign='start'
            fontSize='15px'
            fontWeight='semibold'
            color='red.500'
            noOfLines={2}
            onClick={onOpen}>
            {family.universalName}
          </Text>
          <Box mt='2px'>
            <FamilyCategoryCell
              catalogCategoryId={family.catalogCategoryId}
              categoryTree={categoryTree}
              noOfLines={1}
            />
          </Box>
          <Text fontSize='12px' color='gray.500' mt='2px'>
            {dateLine}
          </Text>
        </Box>
      </Flex>

      <HStack mt='6px' spacing='0'>
        <HStack spacing='2'>
          <Text fontSize='12px' color='gray.500'>
            Active
          </Text>
          <FamilyActiveCell family={family} />
        </HStack>
        <Spacer />
        <IconButtonWithTooltip
          size='sm'
          minW='42px'
          minH='42px'
          label='Open variants'
          aria-label='view-variants-btn'
          Icon={<Icon as={FiList} />}
          variant='unstyled'
          onClick={onOpen}
        />
        <EditFamilyModal
          family={family}
          trigger={
            <IconButtonWithTooltip
              size='sm'
              minW='42px'
              minH='42px'
              label='Edit'
              aria-label='edit-family-btn'
              Icon={<PenIcon />}
              variant='unstyled'
            />
          }
        />
        <IconButtonWithTooltip
          size='sm'
          minW='42px'
          minH='42px'
          label='Merge into…'
          aria-label='merge-family-btn'
          Icon={<Icon as={FiGitMerge} />}
          variant='unstyled'
          onClick={onMerge}
        />
        <DeleteFamilyModal
          family={family}
          trigger={
            <IconButtonWithTooltip
              size='sm'
              minW='42px'
              minH='42px'
              label='Delete'
              aria-label='delete-family-btn'
              Icon={<Icon as={FiTrash2} />}
              variant='unstyled'
            />
          }
        />
      </HStack>
    </Box>
  );
};
